import { parseUploadFormula } from '@erp/shared-utils';
import {
  UploadColumnMappingSchema,
  UploadLookupSchema,
  type UploadLookup,
  uploadLayoutColumnProblems,
  type UploadColumn,
  type UploadColumnMapping,
  type UploadLayout,
  type UploadLayoutDefinition,
} from '@erp/rpc';

/**
 * The layout editor keeps every mapping as plain text fields; this module turns
 * a row of them into a contract-valid mapping (and back), so the editor stays
 * JSX and the translation is tested once.
 */

export type MappingKind = UploadColumnMapping['kind'];
export type SourceFormat = 'text' | 'date' | 'number' | 'substring';

export interface MappingDraft {
  /** Stable React key; not saved. */
  key: string;
  column: UploadColumn;
  kind: MappingKind;
  /** `constant` value, or the value a `flag` writes. */
  value: string;
  /** `source` header, or the header a `flag` watches. */
  header: string;
  format: SourceFormat;
  decimals: string;
  start: string;
  length: string;
  /** A flag watches a workbook header, or another upload column. */
  flagOn: 'header' | 'column';
  flagColumn: UploadColumn;
  /** A source written only when this upload column is filled; '' = always. */
  onlyWhenFilled: UploadColumn | '';
  expression: string;
  blankWhenZero: boolean;
}

export interface LayoutDraft {
  name: string;
  /** Comma-separated workbook headers. */
  includeWhenAnyFilled: string;
  /** Comma-separated workbook headers whose 0 skips the row. */
  skipWhenZero?: string;
  /** Comma-separated workbook headers whose blank skips the row. */
  skipWhenBlank?: string;
  /** A second file matched on a key; all three blank = none. */
  lookupLabel?: string;
  lookupMatchColumn?: string;
  lookupKeyColumn?: string;
  columns: MappingDraft[];
}

/** The errors-map key for a problem with the lookup file settings. */
export const LOOKUP_ERROR_KEY = 'lookup';

function newKey(): string {
  return crypto.randomUUID();
}

export function emptyMappingDraft(column: UploadColumn): MappingDraft {
  return {
    key: newKey(),
    column,
    kind: 'source',
    value: 'Y',
    header: '',
    format: 'text',
    decimals: '2',
    start: '0',
    length: '',
    flagOn: 'header',
    flagColumn: 'A',
    onlyWhenFilled: '',
    expression: '',
    blankWhenZero: false,
  };
}

function wholeNumber(text: string): number {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
}

function sourceTransform(draft: MappingDraft): unknown {
  switch (draft.format) {
    case 'text':
      return { type: 'text' };
    case 'date':
      return { type: 'date' };
    case 'number':
      return { type: 'number', decimals: wholeNumber(draft.decimals) };
    case 'substring':
      return {
        type: 'substring',
        start: wholeNumber(draft.start),
        length: draft.length.trim() === '' ? null : wholeNumber(draft.length),
      };
  }
}

function candidateMapping(draft: MappingDraft): unknown {
  switch (draft.kind) {
    case 'constant':
      return { kind: 'constant', value: draft.value };
    case 'rowNumber':
      return { kind: 'rowNumber' };
    case 'source':
      return {
        kind: 'source',
        header: draft.header,
        transform: sourceTransform(draft),
        ...(draft.onlyWhenFilled === ''
          ? {}
          : { onlyWhenFilled: draft.onlyWhenFilled }),
      };
    case 'flag':
      return {
        kind: 'flag',
        when:
          draft.flagOn === 'header'
            ? { header: draft.header }
            : { column: draft.flagColumn },
        value: draft.value,
      };
    case 'formula':
      return {
        kind: 'formula',
        expression: draft.expression,
        // Blank = written as computed, like a formula with no ROUND.
        decimals:
          draft.decimals.trim() === '' ? null : wholeNumber(draft.decimals),
        blankWhenZero: draft.blankWhenZero,
      };
  }
}

/** Why a draft row can't be saved, for the editor to show next to it. */
export type MappingDraftError =
  | { field: 'header' | 'value' | 'number' }
  /** A parser or layout-level message, shown as is. */
  /** The lookup settings are half filled or the name is invalid. */
  | { field: 'lookup' }
  | { field: 'formula' | 'layout'; message: string };

function fieldForKind(draft: MappingDraft): MappingDraftError {
  if (draft.kind === 'formula') return { field: 'number' };
  if (draft.kind === 'source' && draft.header.trim() !== '') {
    return { field: 'number' };
  }
  if (
    draft.kind === 'source' ||
    (draft.kind === 'flag' && draft.flagOn === 'header')
  ) {
    return { field: 'header' };
  }
  return { field: 'value' };
}

export function mappingFromDraft(
  draft: MappingDraft,
): { mapping: UploadColumnMapping } | { error: MappingDraftError } {
  if (draft.kind === 'formula') {
    const parsed = parseUploadFormula(draft.expression);
    if (!parsed.ok)
      return { error: { field: 'formula', message: parsed.error } };
  }
  const result = UploadColumnMappingSchema.safeParse(candidateMapping(draft));
  return result.success
    ? { mapping: result.data }
    : { error: fieldForKind(draft) };
}

export function definitionFromDraft(
  draft: LayoutDraft,
):
  | { definition: UploadLayoutDefinition }
  | { errors: Map<string, MappingDraftError> } {
  const errors = new Map<string, MappingDraftError>();
  const columns: UploadLayoutDefinition['columns'] = [];
  for (const row of draft.columns) {
    const result = mappingFromDraft(row);
    if ('error' in result) errors.set(row.key, result.error);
    else columns.push({ column: row.column, mapping: result.mapping });
  }
  if (errors.size > 0) return { errors };
  // Problems between rows: a column mapped twice, a flag that can never fire.
  for (const { index, message } of uploadLayoutColumnProblems(columns)) {
    const key = draft.columns[index]?.key;
    if (key) errors.set(key, { field: 'layout', message });
  }
  const lookup = lookupFromDraft(draft);
  if (lookup === undefined) {
    errors.set(LOOKUP_ERROR_KEY, { field: 'lookup' });
  }
  if (errors.size > 0 || lookup === undefined) return { errors };
  return {
    definition: {
      includeWhenAnyFilled: headerList(draft.includeWhenAnyFilled),
      skipWhenZero: headerList(draft.skipWhenZero ?? ''),
      skipWhenBlank: headerList(draft.skipWhenBlank ?? ''),
      lookup,
      columns,
    },
  };
}

function headerList(text: string): string[] {
  return text
    .split(',')
    .map((header) => header.trim())
    .filter((header) => header !== '');
}

/** The lookup settings, null when all blank, undefined when half-filled or invalid. */
function lookupFromDraft(draft: LayoutDraft): UploadLookup | null | undefined {
  const fields = [
    draft.lookupLabel ?? '',
    draft.lookupMatchColumn ?? '',
    draft.lookupKeyColumn ?? '',
  ].map((field) => field.trim());
  if (fields.every((field) => field === '')) return null;
  const [label, matchColumn, keyColumn] = fields;
  const parsed = UploadLookupSchema.safeParse({
    label,
    matchColumn,
    keyColumn,
  });
  return parsed.success ? parsed.data : undefined;
}

function sourceDraftFields(
  mapping: Extract<UploadColumnMapping, { kind: 'source' }>,
): Partial<MappingDraft> {
  const { transform } = mapping;
  const fields: Partial<MappingDraft> = {
    header: mapping.header,
    format: transform.type,
    onlyWhenFilled: mapping.onlyWhenFilled ?? '',
  };
  if (transform.type === 'number') fields.decimals = String(transform.decimals);
  if (transform.type === 'substring') {
    fields.start = String(transform.start);
    fields.length = transform.length === null ? '' : String(transform.length);
  }
  return fields;
}

function flagDraftFields(
  mapping: Extract<UploadColumnMapping, { kind: 'flag' }>,
): Partial<MappingDraft> {
  if ('header' in mapping.when) {
    return {
      flagOn: 'header',
      header: mapping.when.header,
      value: mapping.value,
    };
  }
  return {
    flagOn: 'column',
    flagColumn: mapping.when.column,
    value: mapping.value,
  };
}

function draftFields(mapping: UploadColumnMapping): Partial<MappingDraft> {
  switch (mapping.kind) {
    case 'constant':
      return { value: mapping.value };
    case 'rowNumber':
      return {};
    case 'source':
      return sourceDraftFields(mapping);
    case 'flag':
      return flagDraftFields(mapping);
    case 'formula':
      return {
        expression: mapping.expression,
        decimals: mapping.decimals === null ? '' : String(mapping.decimals),
        blankWhenZero: mapping.blankWhenZero,
      };
  }
}

export function draftFromDefinition(
  name: string,
  definition: UploadLayoutDefinition,
): LayoutDraft {
  return {
    name,
    includeWhenAnyFilled: definition.includeWhenAnyFilled.join(', '),
    skipWhenZero: (definition.skipWhenZero ?? []).join(', '),
    skipWhenBlank: (definition.skipWhenBlank ?? []).join(', '),
    lookupLabel: definition.lookup?.label ?? '',
    lookupMatchColumn: definition.lookup?.matchColumn ?? '',
    lookupKeyColumn: definition.lookup?.keyColumn ?? '',
    columns: definition.columns.map(({ column, mapping }) => ({
      ...emptyMappingDraft(column),
      kind: mapping.kind,
      ...draftFields(mapping),
    })),
  };
}

export function draftFromLayout(layout: UploadLayout): LayoutDraft {
  return draftFromDefinition(layout.name, layout.definition);
}
